import { useEffect, useRef, useState } from 'react'

import styled from '@emotion/styled'
import { useFormContext } from 'react-hook-form'
import { useTranslation } from 'react-i18next'
import NumberFormat from 'react-number-format'

import { ArcadeIcon, CurrencyUsdIcon, PendingIcon } from '@arcade/web/icons/react'
import { ArcadeInput, Buttonless, Grid, spacing, Stack, Text } from '@cash-design-system/react'
import { convertDecimalMoneyToCents } from '@cash-web/shared-util-formatting'
import { Currency, moneyFormatter } from '@cash-web/shared-util-formatting'

interface MoneyOption {
  value: number
  label: string
}

const MoneyButton = styled(Buttonless)<{ isActive: boolean }>`
  border-radius: ${({ theme }) => theme.border.radius.medium};
  padding: ${spacing.smallGap} ${spacing.l};
  display: flex;
  justify-content: center;
  align-items: center;

  ${({ isActive, theme }) =>
    isActive
      ? `box-shadow: 0 0 0 2px ${theme.arcadeColors.component.inputBorderSelected}`
      : `box-shadow: 0 0 0 1px ${theme.arcadeColors.component.inputBorderDefault}`}
`

const DEFAULT_MONEY_OPTIONS = [
  { value: 500, label: '$5' },
  { value: 1000, label: '$10' },
  { value: 2500, label: '$25' },
  { value: 5000, label: '$50' },
  { value: 10000, label: '$100' },
]

type MoneyPickerProps = {
  initialValue?: number
  inputName: string
  isRequired: boolean
  displayCustomAmountOption: boolean
  onValueChange?: (value: string) => void
  minCustomInputAmount: number
  maxCustomInputAmount: number
  options?: MoneyOption[]
}

export const MoneyPicker = ({
  inputName,
  isRequired,
  onValueChange,
  minCustomInputAmount,
  maxCustomInputAmount,
  options = DEFAULT_MONEY_OPTIONS,
  displayCustomAmountOption,
  initialValue,
}: MoneyPickerProps) => {
  const { t } = useTranslation()
  const initialValueMatchesPreset = options.some(option => option.value === initialValue)
  const [showCustomAmount, setShowCustomAmount] = useState(
    displayCustomAmountOption && !!initialValue && !initialValueMatchesPreset
  )
  const customAmountInputRef = useRef<HTMLInputElement>(null)
  const { formatMoney } = moneyFormatter({ currency: Currency.USD })
  const {
    setValue,
    watch,
    register,
    formState: { errors },
    trigger,
  } = useFormContext()

  // Automatically focus the custom amount input when it is shown.
  useEffect(() => {
    if (showCustomAmount && customAmountInputRef.current) {
      customAmountInputRef.current.focus()
    }
  }, [showCustomAmount])

  const amount = Number(watch(inputName))

  register(inputName, {
    value: initialValue && (initialValueMatchesPreset || displayCustomAmountOption) ? initialValue.toString() : '',
    validate: (value: string) => {
      if (value === '') {
        return true
      }

      const valueAsNumber = Number(value)

      if (valueAsNumber > maxCustomInputAmount) {
        return t('Amount must be less than or equal to {maxAmount}.', {
          context: 'inline error message',
          maxAmount: formatMoney(maxCustomInputAmount),
        })
      }
      if (valueAsNumber < minCustomInputAmount) {
        return t('Amount must be greater than {minAmount}.', {
          minAmount: formatMoney(minCustomInputAmount),
          context: 'inline error message',
        })
      }
      return true
    },
    required: isRequired,
  })

  return (
    <Stack space={spacing.xl}>
      <Grid columnGap="m" gridTemplateColumns="repeat(3, 1fr)" rowGap="m">
        {options.map(({ label, value }) => (
          <MoneyButton
            isActive={value === amount}
            key={value}
            onClick={() => {
              onValueChange?.(value.toString())
              setValue(inputName, value)
              setShowCustomAmount(false)
              trigger([inputName])
            }}
            type="button"
          >
            <Text typography="labelMedium" color="inputTextDefault">
              {label}
            </Text>
          </MoneyButton>
        ))}
        {displayCustomAmountOption && (
          <MoneyButton
            data-testid="custom-amount-button"
            onClick={() => {
              onValueChange?.('CUSTOM')
              setShowCustomAmount(prev => !prev)
              setValue(inputName, '')
              trigger([inputName])
            }}
            isActive={showCustomAmount}
          >
            <PendingIcon variant="prominent" />
          </MoneyButton>
        )}
      </Grid>

      {showCustomAmount && displayCustomAmountOption && (
        <Stack space={spacing.xs}>
          <NumberFormat
            customInput={ArcadeInput}
            placeholder="0.00"
            inputMode="decimal"
            LeadingIcon={CurrencyUsdIcon as ArcadeIcon}
            allowNegative={false}
            allowLeadingZeros={false}
            value={amount ? amount / 100 : ''}
            decimalScale={2}
            decimalSeparator="."
            thousandSeparator=","
            isInvalid={!!errors[inputName]}
            errorText={errors[inputName]?.message}
            getInputRef={customAmountInputRef}
            shouldShowErrorIcon={errors[inputName]?.message}
            onValueChange={target => {
              const value = target.value === '' ? '' : convertDecimalMoneyToCents({ decimalAmount: target.value })
              setValue(inputName, value)
              trigger([inputName])
            }}
          />
        </Stack>
      )}
    </Stack>
  )
}
